import parse from '@customink/html-react-parser';
import {Box, Link, Skeleton, Stack, Typography} from '@customink/pigment-react';
import {useTheme} from '@mui/material/styles';
import {ErrorBoundary} from '@rollbar/react';
import React, {useContext} from 'react';
import SomethingWentWrongFallbackUI from 'components/SomethingWentWrongFallbackUI';
import useProductDeliveryOptions from 'hooks/queries/productDeliveryOptions';
import ProductContext from 'product_details_page/Context/productContext';

const DeliveryOptionsSkeleton = () => (
  <ul style={{marginBottom: 0}}>
    <li>
      <Box sx={{alignItems: 'center', display: 'flex'}}>
        <Skeleton variant="text" width="100%" height="1.25rem" />
      </Box>
    </li>
    <li>
      <Box sx={{alignItems: 'center', display: 'flex'}}>
        <Skeleton variant="text" width="85%" height="1.25rem" />
      </Box>
    </li>
    <li>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
        <Skeleton variant="text" width="90%" height="1.25rem" />
        <Skeleton variant="text" width="60%" height="1rem" />
      </Box>
    </li>
  </ul>
);

const DeliveryOptionsError = () => (
  <Typography variant="bodyLong1">
    Something went wrong while fetching delivery options. Please try again
    later.
  </Typography>
);

const DeliveryOptionsContent = ({
  deliveryText,
  individualShipEligible,
  rushDeliveryText
}) => {
  const theme = useTheme();

  return (
    <ul style={{lineHeight: '1.25rem', marginBottom: 0}}>
      {deliveryText && (
        <li>
          <Typography variant="bodyLong1">
            <strong>Free </strong>
            {parse(deliveryText)}
          </Typography>
        </li>
      )}
      {rushDeliveryText && (
        <li>
          <Typography variant="bodyLong1">{parse(rushDeliveryText)}</Typography>
        </li>
      )}
      {individualShipEligible && (
        <li>
          <Typography variant="bodyLong1">
            <Box
              component="strong"
              sx={{
                color: theme.palette.red.dark,
                fontWeight: theme.typography.fontWeightBold
              }}>
              SHIP TO MULTIPLE ADDRESSES
            </Box>{' '}
            - Flat rate shipping is $9.95 per US address
            <br />
            <Link
              variant="bodyLong1"
              sx={{
                fontWeight: 'normal',
                textDecoration: 'underline'
              }}
              className="pc-Style-BssAwareness-link"
              href="#ship-to-multiple-addresses"
              aria-label="Learn more about shipping to multiple addresses"
              data-testid="ship-to-multiple-addresses">
              Learn More
            </Link>
          </Typography>
        </li>
      )}
    </ul>
  );
};

const DeliveryOptions = () => {
  const {id: styleId} = useContext(ProductContext);
  const {
    deliveryText,
    error,
    individualShipEligible,
    isLoading,
    rushDeliveryText
  } = useProductDeliveryOptions(styleId);

  return (
    <ErrorBoundary fallbackUI={SomethingWentWrongFallbackUI}>
      <Stack gap="1rem" sx={{paddingBottom: '1rem'}}>
        <Typography variant="h6">Delivery Options</Typography>
        {error && <DeliveryOptionsError />}
        {isLoading && <DeliveryOptionsSkeleton />}
        {!error && !isLoading && (
          <DeliveryOptionsContent
            deliveryText={deliveryText}
            rushDeliveryText={rushDeliveryText}
            individualShipEligible={individualShipEligible}
          />
        )}
      </Stack>
    </ErrorBoundary>
  );
};

export default DeliveryOptions;
